/**
 * Token Refresh Service
 *
 * Singleton service that handles refreshing the authentication token
 * when API calls receive a 401 response.
 *
 * Key features:
 * - Calls POST /api/v1/auth/refresh (same-origin, cookie-based)
 * - Deduplicates concurrent refresh requests (only one in-flight at a time)
 * - Queues callers while a refresh is in progress
 * - On failure, extracts logout_url from the response if present
 * - Triggers a session-expired callback so the UI can show an overlay
 */
import { API_CONFIG } from '@/config/api';

/** Result of a refresh attempt */
export interface RefreshResult {
  /** Whether the refresh succeeded */
  success: boolean;
  /** Logout URL returned by the server on 401, if any */
  logoutUrl?: string;
  /**
   * HTTP status of the refresh response. Present whenever the server
   * actually responded (i.e. not a network error). Lets callers
   * distinguish a genuine "session is gone" 401 from a transient
   * failure — the proactive refresh only escalates to session-expired
   * on a 401, not on a flaky 5xx or network blip.
   */
  status?: number;
  /**
   * When the freshly-issued session expires, as returned by /auth/refresh
   * (`{ "ok": true, "expiresAt": "<RFC3339>" }`). Lets the proactive refresh
   * schedule the next refresh straight off the new token — no extra /me
   * round-trip. Absent if the response carried no expiry.
   */
  expiresAt?: string;
}

/**
 * Callback invoked when the session has expired and refresh has failed.
 * The UI layer registers a listener via `onSessionExpired()`.
 */
type SessionExpiredListener = (logoutUrl?: string) => void;

let sessionExpiredListener: SessionExpiredListener | null = null;

/**
 * Register a listener that will be called when the session expires
 * (i.e., token refresh fails). Only one listener is supported —
 * typically set once at app startup.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListener = listener;
  return () => {
    if (sessionExpiredListener === listener) {
      sessionExpiredListener = null;
    }
  };
}

/** Notify the registered listener */
function notifySessionExpired(logoutUrl?: string): void {
  sessionExpiredListener?.(logoutUrl);
}

/** Internal state for the singleton */
let refreshInProgress: Promise<RefreshResult> | null = null;

/**
 * Attempt to refresh the authentication token by calling the
 * compute-api refresh endpoint. The server reads the httpOnly
 * cookie, contacts the control-plane, and sets a new e6_session cookie.
 *
 * Concurrent callers are deduplicated — only one network request
 * is made, and all callers share the same promise.
 *
 * @returns RefreshResult with success flag and optional logoutUrl
 */
export async function refreshToken(): Promise<RefreshResult> {
  // If a refresh is already in flight, piggyback on it
  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = doRefresh();

  try {
    return await refreshInProgress;
  } finally {
    // Clear so the next 401 can trigger a new refresh
    refreshInProgress = null;
  }
}

/**
 * Perform the actual refresh network call.
 * On failure, tries to parse the response body for a logout_url.
 */
async function doRefresh(): Promise<RefreshResult> {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.apiVersion}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // send httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      // Read the new token's expiry from the response so callers can size the
      // next refresh without a separate /me read.
      let expiresAt: string | undefined;
      try {
        const body = await response.json();
        expiresAt = body?.expiresAt;
      } catch {
        // Body isn't JSON or is empty — that's fine, expiresAt stays undefined.
      }
      return { success: true, status: response.status, expiresAt };
    }

    // Refresh failed — try to extract logout_url from response body
    let logoutUrl: string | undefined;
    try {
      const body = await response.json();
      logoutUrl = body?.logout_url || body?.logoutUrl;
    } catch {
      // Response body isn't JSON or is empty — that's fine
    }

    return { success: false, logoutUrl, status: response.status };
  } catch {
    // Network error — no response, so no status to report
    return { success: false };
  }
}

/**
 * Handle session expiration: clear client-side storage and notify UI.
 * Called by API layers (client.ts, api.ts, statementExecutionService.ts)
 * when refresh fails.
 */
export function handleSessionExpired(logoutUrl?: string): void {
  // Clear any client-side storage
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Storage access may fail in some contexts — ignore
  }

  // Notify the UI to show session-expired overlay
  notifySessionExpired(logoutUrl);
}

/**
 * Check whether a refresh is currently in progress.
 * Useful for tests and debugging.
 */
export function isRefreshInProgress(): boolean {
  return refreshInProgress !== null;
}

/**
 * Reset all state. Only intended for testing.
 * @internal
 */
export function _resetForTesting(): void {
  refreshInProgress = null;
  sessionExpiredListener = null;
}
